import { z } from 'zod';

const timestamp = z.string().datetime({ offset: true });

export const workerSafetyZonePresenceSchema = z.object({
  zone_id: z.string().uuid(),
  name: z.string(),
  severity: z.enum(['WARNING', 'DANGER']),
  entry_id: z.string().uuid(),
  entered_at: timestamp,
});

export const workerSafetyStateSchema = z.object({
  status: z.enum(['UNKNOWN', 'CLEAR', 'WARNING', 'DANGER']),
  reason: z.enum(['NO_POSITION', 'STALE_POSITION', 'SESSION_INACTIVE', 'INVALID_SETTINGS']).nullable(),
  settings_revision: z.number().int().min(-1).safe(),
  evaluated_at: timestamp.nullable(),
  valid_until: timestamp.nullable(),
  zones: z.array(workerSafetyZonePresenceSchema),
});

export const workerSafetyEventSchema = z
  .object({
    entity_type: z.literal('MOBILE_SAFETY_EVENT'),
    event_id: z.string().uuid(),
    runtime_key: z.string(),
    version: z.literal(1),
    field_uid: z.string().uuid(),
    device_uid: z.string().uuid(),
    user_uid: z.string().uuid(),
    session_uid: z.string().uuid(),
    user_name: z.string(),
    routing_version: z.number().int().positive(),
    ownership_epoch: z.number().int().positive(),
    occurred_at: timestamp,
    observed_at: timestamp,
    transition: z.enum(['ENTER', 'EXIT']),
    reason: z.enum(['POSITION', 'CONFIGURATION']),
    severity: z.enum(['WARNING', 'DANGER']),
    zones: z.array(workerSafetyZonePresenceSchema).min(1),
  })
  .refine(event => event.runtime_key === `mobile-safety:${event.event_id}`, { message: '안전구역 이벤트 식별자가 일치하지 않습니다.' });

export type WorkerSafetyEvent = z.infer<typeof workerSafetyEventSchema>;

export const workerMobileProjectionSchema = z
  .object({
    entity_type: z.literal('MOBILE_DEVICE'),
    runtime_key: z.string(),
    field_uid: z.string().uuid(),
    version: z.number().int().nonnegative().safe(),
    routing_version: z.number().int().positive(),
    ownership_epoch: z.number().int().positive(),
    user_uid: z.string().uuid(),
    user_name: z.string(),
    company_name: z.string().nullable(),
    job_title: z.string().nullable(),
    device_uid: z.string().uuid(),
    device_alias: z.string().nullable(),
    session_uid: z.string().uuid(),
    session_started_at: timestamp,
    session_status: z.enum(['ACTIVE', 'LOGGED_OUT', 'EXPIRED', 'REVOKED']),
    receive_status: z.enum(['WAITING', 'ONLINE', 'STALE', 'DEAD']),
    position_status: z.enum(['NONE', 'FRESH', 'STALE']),
    latitude: z.number().finite().min(-90).max(90).nullable(),
    longitude: z.number().finite().min(-180).max(180).nullable(),
    battery: z.number().int().min(0).max(100).nullable(),
    platform: z.enum(['android', 'ios']),
    os_version: z.string(),
    observed_at: timestamp.nullable(),
    received_at: timestamp.nullable(),
    last_live_received_at: timestamp.nullable(),
    stale_at: timestamp,
    dead_at: timestamp,
    server_time: timestamp,
    is_visible: z.boolean(),
    marker_icon_url: z.string().nullable(),
    safety_state: workerSafetyStateSchema.optional(),
  })
  .refine(device => device.runtime_key === `mobile:${device.device_uid}`, { message: '모바일 마커 식별자가 일치하지 않습니다.' });

export type WorkerMobileProjection = z.infer<typeof workerMobileProjectionSchema>;

export const workerMobileMovementPointSchema = z.object({
  event_id: z.string().uuid(),
  field_uid: z.string().uuid(),
  device_uid: z.string().uuid(),
  user_uid: z.string().uuid(),
  observed_at: timestamp,
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
});
